/**
 * ReusableFooter Component
 *
 * Three-line footer layout, centered:
 *   Row 1: NavBarLogo
 *   Row 2: Tagline
 *   Row 3: Legal links (Terms | Privacy | Copyright)
 *
 * Transparent background with inherited color — works on any page background.
 * No props — all content is static.
 *
 * Requirements: 1.1–1.5, 2.1–2.4, 3.1–3.3, 4.1–4.3
 */

import { Box, Typography, Link } from '@mui/material';
import { NavBarLogo } from './NavBarLogo';

export const LEGAL_LINKS = [
    { label: 'Terms', href: '/terms' },
    { label: 'Privacy', href: '/privacy' },
    { label: 'DMCA', href: '/dmca' },
] as const;

export const COPYRIGHT_LINE = '© 2026 VoiceRecipe';

export const TAGLINE = 'AI voice for everyone';

export function ReusableFooter() {
    return (
        <Box
            component="footer"
            sx={{
                backgroundColor: 'transparent',
                color: 'inherit',
                textAlign: 'center',
                width: '100%',
                mt: 6,
                pt: 2,
                pb: 4,
                px: 2,
                mx: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                borderTop: 1,
                borderColor: 'divider',
            }}
        >
            {/* Row 1: Logo */}
            <Box sx={{ mb: 1 }}>
                <NavBarLogo size="medium" />
            </Box>

            {/* Row 2: Tagline */}
            <Typography variant="body2" sx={{ mb: 1, fontSize: '16px' }}>
                {TAGLINE}
            </Typography>

            {/* Row 3: Legal links separated by pipes */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', fontSize: '15px', color: 'grey.500' }}>
                {LEGAL_LINKS.map((link, index) => (
                    <Box key={link.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {index > 0 && (
                            <Typography variant="caption" component="span">
                                |
                            </Typography>
                        )}
                        <Link
                            href={link.href}
                            underline="hover"
                            sx={{ color: 'inherit', fontSize: 'inherit' }}
                        >
                            {link.label}
                        </Link>
                    </Box>
                ))}
            </Box>

            {/* Row 4: Copyright */}
            <Typography variant="caption" sx={{ mt: 1, color: 'grey.500' }}>
                {COPYRIGHT_LINE}
            </Typography>
        </Box>
    );
}
